import { useQuery } from '@tanstack/react-query';
import { type LocationDescriptor } from 'history';
import { useEffect, useRef } from 'react';

import { api, getQueryProps, isDefined, useLoggedInAuthContext } from 'common';

import { useBrowserNotificationsContext } from './useBrowserNotifications.js';
import { FeatureFlagKey, useFlag } from '../../utils/flags/index.js';
import { getPathFromLocationDescriptor } from '../../utils/navigation.js';

const DEFAULT_POLL_INTERVAL_MS = 15_000 as const;

function parseTimestampMilliseconds(value: string | null): number | null {
  if (!isDefined(value)) {
    return null;
  }
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) ? milliseconds : null;
}

function buildAgentConversationLocationDescriptor(
  notification: api.BrowserNotification.AgentConversationBrowserNotification
): LocationDescriptor {
  return {
    pathname: `/entity/patients/${notification.recipientEhrInformationId}`,
    search: `tab=agentConversation&agentConversationId=${notification.id}`,
  };
}

function emitDesktopNotification(params: {
  title: string;
  body: string;
  locationDescriptor: LocationDescriptor;
}) {
  const { title, body, locationDescriptor } = params;
  const url = getPathFromLocationDescriptor(locationDescriptor);
  const notification = new Notification(title, {
    body,
    data: { url },
  });

  notification.onclick = (event) => {
    event.preventDefault();
    try {
      notification.close();
    } catch (error) {
      console.error('Error closing notification:', error);
    }
    window.open(`#${url}`, '_blank');
  };
}

/**
 * This hook polls for new notifications and emits desktop notifications.
 *
 * The notifications processing `useEffect` in this hook depends on:
 * - `canProcessNotificationResults` (derived from permissions, feature flags, and query state),
 * - `notificationsResults.data.data` (the latest batch of notifications),
 * - `notificationTitle` / `notificationBody` (feature flag values),
 * - `memoizedSetWatermarkMilliseconds` (context setter used when advancing the watermark).
 *
 * The `useQuery` call also depends on `watermarkMilliseconds` because it is used to build
 * the `afterTimestamp` parameter, so changes to the watermark change the query inputs.
 *
 * Circular logic:
 *  - the effect advances the watermark via `memoizedSetWatermarkMilliseconds`
 *  - this updates `watermarkMilliseconds` and therefore the query's `afterTimestamp`.
 *  - this can change `notificationsResults.data.data`, re-triggering the effect.
 *     - The effect relies on `watermarkMillisecondsRef` and per-conversation tracking to avoid re-notifying on the same items while still allowing new activity to notify.
 */
export function useBrowserNotificationsPoller() {
  const {
    isEnabled: isFeatureFlagEnabled,
    notificationPollingIntervalInMilliseconds,
    notificationTitle,
    notificationBody,
  } = useFlag({
    flagKey: FeatureFlagKey.browserNotifications,
  });
  const { canEmitNotifications, watermarkMilliseconds, memoizedSetWatermarkMilliseconds } =
    useBrowserNotificationsContext();
  const { permissions, isAdminRole, isStaffRole } = useLoggedInAuthContext();

  // Track the last timestamp we notified for *each* conversation id, so we can notify again
  // if the same conversation gets new activity later.
  // (We still use `watermarkMs` as a baseline to avoid notifying on backlog after refresh.)
  const lastNotifiedMillisecondsByConversationIdRef = useRef<Map<string, number>>(new Map());

  const notificationsResults = useQuery({
    ...getQueryProps(api.client.Notifications, 'findAll', {
      afterTimestamp: new Date(watermarkMilliseconds).toISOString(),
      page: { size: 100 },
    }),
    enabled:
      (isAdminRole || isStaffRole) &&
      permissions.includes(api.Group.Permission.ModifyAgentConversation) &&
      isFeatureFlagEnabled,
    refetchInterval: notificationPollingIntervalInMilliseconds ?? DEFAULT_POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  const watermarkMillisecondsRef = useRef(watermarkMilliseconds);

  useEffect(() => {
    watermarkMillisecondsRef.current = watermarkMilliseconds;
  }, [watermarkMilliseconds]);

  const canProcessNotificationResults =
    canEmitNotifications &&
    !notificationsResults.isPending &&
    !notificationsResults.isError &&
    notificationsResults.data.data.length > 0;

  useEffect(() => {
    if (!canProcessNotificationResults) {
      return;
    }

    const lastNotifiedMillisecondsByConversationId =
      lastNotifiedMillisecondsByConversationIdRef.current;

    let maxTimestampMilliseconds = watermarkMillisecondsRef.current;
    const conversationNotifications = notificationsResults.data.data;
    for (const conversationNotification of conversationNotifications) {
      const eventTimestampMilliseconds =
        parseTimestampMilliseconds(conversationNotification.lastRecipientRepliedTimestamp) ??
        parseTimestampMilliseconds(conversationNotification.created);
      if (!isDefined(eventTimestampMilliseconds)) {
        continue;
      }

      const lastNotifiedForConversationMilliseconds =
        lastNotifiedMillisecondsByConversationId.get(conversationNotification.id) ??
        watermarkMillisecondsRef.current;
      if (eventTimestampMilliseconds <= lastNotifiedForConversationMilliseconds) {
        continue;
      }

      const agentConversationLocationDescriptor =
        buildAgentConversationLocationDescriptor(conversationNotification);

      emitDesktopNotification({
        title: notificationTitle,
        body: notificationBody,
        locationDescriptor: agentConversationLocationDescriptor,
      });

      lastNotifiedMillisecondsByConversationId.set(
        conversationNotification.id,
        eventTimestampMilliseconds
      );
      maxTimestampMilliseconds = Math.max(maxTimestampMilliseconds, eventTimestampMilliseconds);
    }

    if (maxTimestampMilliseconds !== watermarkMillisecondsRef.current) {
      watermarkMillisecondsRef.current = maxTimestampMilliseconds;
      memoizedSetWatermarkMilliseconds(maxTimestampMilliseconds);
    }
  }, [
    canProcessNotificationResults,
    notificationsResults.data?.data,
    notificationTitle,
    notificationBody,
    memoizedSetWatermarkMilliseconds,
  ]);
}
